// B2B Auth Context
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const B2BContext = createContext(null);

export function B2BProvider({ children }) {
  const [user, setUser] = useState(null);
  const [agency, setAgency] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('b2b_token'));

  // Get token helper (always returns current value)
  const getToken = useCallback(() => {
    return localStorage.getItem('b2b_token');
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = getToken();
      if (storedToken) {
        try {
          const res = await fetch('/api/b2b/auth/me', {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            setAgency(data.agency);
            setSubscription(data.subscription);
          } else {
            // Token invalid, clear it
            console.log('Session check failed, clearing token');
            localStorage.removeItem('b2b_token');
            setToken(null);
          }
        } catch (err) {
          console.error('Session check error:', err);
          localStorage.removeItem('b2b_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    
    checkSession();
  }, [getToken]);

  const login = async (email, password) => {
    const res = await fetch('/api/b2b/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }
    
    localStorage.setItem('b2b_token', data.session.access_token);
    setToken(data.session.access_token);
    setUser(data.user);
    setAgency(data.agency);
    setSubscription(data.subscription);
    
    return data;
  };

  const signup = async (formData) => {
    const res = await fetch('/api/b2b/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || 'Signup failed');
    }
    
    localStorage.setItem('b2b_token', data.session.access_token);
    setToken(data.session.access_token);
    setUser(data.user);
    setAgency(data.agency);
    setSubscription(data.subscription);
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem('b2b_token');
    setToken(null);
    setUser(null);
    setAgency(null);
    setSubscription(null);
  };

  const refreshAgency = useCallback(async () => {
    const storedToken = getToken();
    if (!storedToken) return;
    
    try {
      const res = await fetch('/api/b2b/agency/settings', {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Settings API returns { settings: {...} } - extract the settings object
        // Also merge with existing agency data to preserve fields not in settings response
        const settingsData = data.settings || data;
        setAgency(prev => prev ? { ...prev, ...settingsData } : settingsData);
      } else {
        console.error('Failed to refresh agency:', res.status);
      }
    } catch (err) {
      console.error('Failed to refresh agency:', err);
    }
  }, [getToken]);

  const value = {
    user,
    agency,
    subscription,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    refreshAgency,
    getToken,
    token
  };

  return (
    <B2BContext.Provider value={value}>
      {children}
    </B2BContext.Provider>
  );
}

export function useB2B() {
  const context = useContext(B2BContext);
  if (!context) {
    throw new Error('useB2B must be used within a B2BProvider');
  }
  return context;
}

export default B2BContext;
